import { twMerge } from 'tailwind-merge'
import { useEnv } from '~/env'
import { UnknownTypeError } from '~/lib/error/unknownTypeError'
import { useIsPC } from '~/lib/ua'
import { css, sva } from '~/styled-system/css'
import {
  useMemberEmailStatus,
  useMemberNameKanaStatus,
  useMemberNameStatus,
  useMemberPhoneNumberStatus,
} from '../../store/userInput'
import { Section } from '../Section'
import { VisitorsInput } from '../visitorsInput'
import { Email, EmailInput } from './Email'
import { NameKana, NameKanaInput } from './NameKana'
import { PhoneNumber, PhoneNumberInput } from './PhoneNumber'

export function Member() {
  const isPC = useIsPC()
  const Member_ = isPC ? PCMember : SDMember
  return <Member_ />
}

const pcMemberStyles = sva({
  slots: ['container', 'head', 'title', 'content', 'information'],
  base: {
    container: {
      bg: 'white',
      p: '6',
      spaceY: '3',
    },
    head: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    title: {
      fontSize: '2xl',
      fontWeight: 'bold',
    },
    content: {
      spaceY: '5',
    },
    information: {
      fontSize: 'md',
    },
  },
})

function PCMember() {
  const { hasValueWithHeader } = useMember()
  const classes = pcMemberStyles()

  return (
    <div className={classes.container}>
      <div className={classes.head}>
        <h2 className={classes.title}>予約者情報</h2>
        <IkyuOnlyMemberSettingsLink />
      </div>
      <div className={classes.content}>
        <div
          className={twMerge(
            classes.information,
            hasValueWithHeader
              ? css({ spaceY: '4', md: { spaceY: '5' } })
              : css({ spaceY: '1', md: { spaceY: '2' } })
          )}
        >
          <IkyuOnlyMemberName />
          <MemberNameKana />
          <MemberPhoneNumber />
          <MemberEmail />
        </div>
        <VisitorsInput />
      </div>
    </div>
  )
}

const sdMemberStyles = sva({
  slots: ['content', 'information'],
  base: {
    content: {
      spaceY: '5',
    },
    information: {
      fontSize: 'md',
    },
  },
})

function SDMember() {
  const { hasValueWithHeader } = useMember()
  const classes = sdMemberStyles()

  return (
    <Section title="予約者情報" annotation={<IkyuOnlyMemberSettingsLink />}>
      <div className={classes.content}>
        <div
          className={twMerge(
            classes.information,
            hasValueWithHeader
              ? css({ spaceY: '4', md: { spaceY: '5' } })
              : css({ spaceY: '1', md: { spaceY: '2' } })
          )}
        >
          <IkyuOnlyMemberName />
          <MemberNameKana />
          <MemberPhoneNumber />
          <MemberEmail />
        </div>
        <VisitorsInput />
      </div>
    </Section>
  )
}

function useMember() {
  const kanaStatus = useMemberNameKanaStatus()
  const phoneNumberStatus = useMemberPhoneNumberStatus()
  const emailStatus = useMemberEmailStatus()
  const hasValueWithHeader =
    kanaStatus.type === 'valueWithHeader' ||
    phoneNumberStatus.type === 'valueWithHeader' ||
    emailStatus.type === 'valueWithHeader'
  return { hasValueWithHeader }
}

const IkyuOnlyMemberSettingsLink =
  import.meta.env.VITE_MODE === 'ppg' ? EmptyMemberSettingsLink : MemberSettingsLink

function MemberSettingsLink() {
  const env = useEnv()
  return (
    <a href={`${env.MEMBER_ORIGIN}/member/settings`} className={css({ color: 'link' })}>
      会員情報の変更
    </a>
  )
}

function EmptyMemberSettingsLink() {
  return null
}

const memberNameStyles = sva({
  slots: ['container', 'heading', 'title', 'required', 'note'],
  base: {
    container: {
      spaceY: '1',
      md: {
        display: 'flex',
        alignItems: 'center',
        spaceY: '0',
        spaceX: '2',
      },
    },
    heading: {
      display: 'flex',
      alignItems: 'center',
      spaceX: '2',
      md: {
        display: 'block',
        spaceX: '0',
        width: '[188px]',
        flexShrink: '0',
        spaceY: '1',
      },
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 'md',
    },
    required: {
      ml: '1',
      fontSize: 'sm',
      color: 'accent.pink',
    },
    note: {
      fontSize: 'xs',
    },
  },
})

// 一休の会員予約の予約入力だけ「氏名（漢字）」を表示する
const IkyuOnlyMemberName = import.meta.env.VITE_MODE === 'ppg' ? EmptyMemberName : MemberName

function EmptyMemberName() {
  return null
}

function MemberName() {
  const status = useMemberNameStatus()
  const classes = memberNameStyles()
  switch (status.type) {
    case 'input':
      return null
    case 'valueOnly':
      // すべて情報が揃っている場合は 氏名（ひらがな）の表示制御している createMemberNameKanaStatus で
      // 氏名（漢字）の表示を制御するため、ここでは何も表示しない
      return null
    case 'valueWithHeader':
      return (
        <div className={classes.container}>
          <div className={classes.heading}>
            <p className={classes.title}>
              氏名（漢字）
              <span className={classes.required}>*</span>
            </p>
            <p className={classes.note}>※フルネーム</p>
          </div>
          <p>{status.value}</p>
        </div>
      )

    default:
      throw new UnknownTypeError(status)
  }
}

function MemberNameKana() {
  const status = useMemberNameKanaStatus()
  switch (status.type) {
    case 'input':
      return (
        <NameKana>
          <NameKanaInput />
        </NameKana>
      )
    case 'valueWithHeader':
      return (
        <NameKana>
          <p>{status.value}</p>
        </NameKana>
      )
    case 'valueOnly':
      return <p className="flex items-center justify-between">{status.value}</p>
    default:
      throw new UnknownTypeError(status)
  }
}

function MemberPhoneNumber() {
  const status = useMemberPhoneNumberStatus()
  switch (status.type) {
    case 'input':
      return (
        <PhoneNumber>
          <PhoneNumberInput />
        </PhoneNumber>
      )
    case 'valueWithHeader':
      return (
        <PhoneNumber>
          <p>{status.value}</p>
        </PhoneNumber>
      )
    case 'valueOnly':
      return <p>{status.value}</p>
    default:
      throw new UnknownTypeError(status)
  }
}

function MemberEmail() {
  const status = useMemberEmailStatus()
  switch (status.type) {
    case 'input':
      return (
        <Email>
          <EmailInput />
        </Email>
      )
    case 'valueWithHeader':
      return (
        <Email>
          <p>{status.value}</p>
        </Email>
      )
    case 'valueOnly':
      return <p>{status.value}</p>
    default:
      throw new UnknownTypeError(status)
  }
}
